import type { ReactNode } from "react";
import React, { useMemo, useState } from "react";

import type { T_ChatV8ThreadListItem } from "@lattice/core-lib/types/chat-v8";

import {
  filterShellThreadsByName,
  getShellThreadDisplayName,
} from "../shell/thread-search";

export type T_ShellHistoryThreadRow = {
  /** Precomputed display label (name or the shared untitled fallback). */
  displayName: string;
  /** Position within the FILTERED list; used for selection analytics. */
  index: number;
  isActive: boolean;
  threadCuid: string;
  /** Raw stored name; rename flows seed their draft from this, not the fallback. */
  threadName: string | null;
};

export type T_ShellHistoryListStatus = "loading" | "ready";

/**
 * Which empty state applies when there are no rows. The RULE is shared;
 * the copy stays platform-owned for now (platforms currently ship different
 * strings, and converging copy is a product decision, not a refactor).
 */
export type T_ShellHistoryEmptyState = "empty" | "loading" | "no-matches";

export type T_ShellHistoryPanelModel = {
  emptyState: T_ShellHistoryEmptyState;
  rows: T_ShellHistoryThreadRow[];
  searchActive: boolean;
};

/**
 * Pure projection of panel inputs to the prepared list model. Exported so the
 * recipe is testable without any renderer, and so both the component below and
 * platform tests share one truth for filtering/empty-state rules.
 */
export function prepareShellHistoryPanelModel(args: {
  activeThreadCuid: string | null;
  searchQuery: string;
  status: T_ShellHistoryListStatus;
  threads: readonly T_ChatV8ThreadListItem[];
}): T_ShellHistoryPanelModel {
  const searchActive = args.searchQuery.trim().length > 0;
  const rows = filterShellThreadsByName(
    [...args.threads],
    args.searchQuery,
  ).map((thread, index) => ({
    displayName: getShellThreadDisplayName(thread),
    index,
    isActive: thread.threadCuid === args.activeThreadCuid,
    threadCuid: thread.threadCuid,
    threadName: thread.threadName,
  }));

  const emptyState: T_ShellHistoryEmptyState =
    args.status === "loading"
      ? "loading"
      : searchActive
        ? "no-matches"
        : "empty";

  return { emptyState, rows, searchActive };
}

/**
 * Feature-level slots each platform supplies. The composer owns section
 * ORDER and the list model; platforms own containers, virtualization,
 * gestures, and styling.
 */
export type T_ShellHistoryPanelSlots = {
  /** Panel chrome (nav/View, open-state classes). Receives ordered sections. */
  renderContainer: (sections: ReactNode[]) => ReactNode;
  /**
   * Platform-specific blocks between the list and the footer (e.g. mobile's
   * legacy-chat migration block). Omission renders nothing.
   */
  renderExtras?: () => ReactNode;
  /** Footer: settings entry (web) or account row (mobile). */
  renderFooter: () => ReactNode;
  /** Brand row; mobile includes its close control here. */
  renderHeader: () => ReactNode;
  /** New-chat control. Active state is passed through for highlight. */
  renderNewChat: (args: { newChatActive: boolean }) => ReactNode;
  /** Search input. Must render the CURRENT query and report edits. */
  renderSearch: (args: {
    onSearchQueryChange: (query: string) => void;
    searchQuery: string;
  }) => ReactNode;
  /**
   * The thread list. Receives the prepared model; platform owns
   * scrolling/virtualization (e.g. FlatList) and row visuals. When rows is
   * empty, the platform maps `emptyState` to its copy.
   */
  renderThreadList: (args: T_ShellHistoryPanelModel) => ReactNode;
};

export type T_ShellHistoryPanelProps = {
  activeThreadCuid: string | null;
  newChatActive: boolean;
  slots: T_ShellHistoryPanelSlots;
  /** Explicit list status; never inferred from threads.length. */
  status?: T_ShellHistoryListStatus;
  threads: readonly T_ChatV8ThreadListItem[];
};

/**
 * Pure section ordering: header -> new chat -> search -> list -> extras? ->
 * footer. Exported separately from the component so the order is testable
 * without a renderer (the component itself owns search state via hooks).
 *
 * Index contract for renderContainer grouping: sections[0..2] are always
 * header/new-chat/search, sections[3] is the list, and the remainder is
 * extras (when provided) then footer. Mobile relies on this to wrap the
 * first three in its padded header block.
 */
export function composeShellHistoryPanelSections(args: {
  model: T_ShellHistoryPanelModel;
  newChatActive: boolean;
  onSearchQueryChange: (query: string) => void;
  searchQuery: string;
  slots: T_ShellHistoryPanelSlots;
}): ReactNode[] {
  const { model, newChatActive, onSearchQueryChange, searchQuery, slots } =
    args;
  const sections: ReactNode[] = [
    <React.Fragment key="header">{slots.renderHeader()}</React.Fragment>,
    <React.Fragment key="new-chat">
      {slots.renderNewChat({ newChatActive })}
    </React.Fragment>,
    <React.Fragment key="search">
      {slots.renderSearch({ onSearchQueryChange, searchQuery })}
    </React.Fragment>,
    <React.Fragment key="list">{slots.renderThreadList(model)}</React.Fragment>,
  ];
  if (slots.renderExtras) {
    sections.push(
      <React.Fragment key="extras">{slots.renderExtras()}</React.Fragment>,
    );
  }
  sections.push(
    <React.Fragment key="footer">{slots.renderFooter()}</React.Fragment>,
  );
  return sections;
}

/**
 * Shared composition for the conversation-history surface: web's desktop
 * rail/narrow drawer and mobile's navigation drawer. Owns the section order
 * (header -> new chat -> search -> list -> extras -> footer), search state,
 * filtering, row preparation, and empty/loading copy. The web rail was
 * documented as a "desktop adaptation of the mobile ChatHistoryDrawer" —
 * this makes that adaptation structural instead of aspirational, and native
 * tablets later inherit the same composition.
 */
export function ShellHistoryPanel({
  activeThreadCuid,
  newChatActive,
  slots,
  status = "ready",
  threads,
}: T_ShellHistoryPanelProps): ReactNode {
  const [searchQuery, setSearchQuery] = useState("");

  const model = useMemo(
    () =>
      prepareShellHistoryPanelModel({
        activeThreadCuid,
        searchQuery,
        status,
        threads,
      }),
    [activeThreadCuid, searchQuery, status, threads],
  );

  return slots.renderContainer(
    composeShellHistoryPanelSections({
      model,
      newChatActive,
      onSearchQueryChange: setSearchQuery,
      searchQuery,
      slots,
    }),
  );
}
